export class PresentationTimeoutError extends Error {
  constructor() {
    super('Presentation request timed out')
    this.name = 'PresentationTimeoutError'
  }
}

/** Total budget for the presentation-only HTTP operation, including auth and body. */
export async function withPresentationDeadline<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  parentSignal?: AbortSignal,
): Promise<T> {
  if (parentSignal) assertPresentationActive(parentSignal)
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  let abort!: () => void
  const deadline = new Promise<never>((_resolve, reject) => {
    abort = () => {
      controller.abort()
      reject(new PresentationTimeoutError())
    }
    parentSignal?.addEventListener('abort', abort, { once: true })
    timer = setTimeout(abort, timeoutMs)
  })
  try {
    return await Promise.race([operation(controller.signal), deadline])
  } finally {
    clearTimeout(timer)
    parentSignal?.removeEventListener('abort', abort)
  }
}

export function assertPresentationActive(signal: AbortSignal) {
  if (signal.aborted) throw new PresentationTimeoutError()
}
